import React from "react";
import { TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, CheckCircle2, XCircle, AlertCircle, Eye, Loader2, RefreshCw } from "lucide-react";

const DOCS = [
  { key: "passport", label: "Paspor" },
  { key: "id_card", label: "KTP" },
  { key: "family_card", label: "KK" },
  { key: "vaccine_cert", label: "Meningitis" },
  { key: "marriage_book", label: "Buku Nikah" },
];

const STATUS_BADGE = {
  pending: { c: "bg-amber-100 text-amber-900", i: AlertCircle, l: "Pending" },
  verified: { c: "bg-emerald-100 text-emerald-800", i: CheckCircle2, l: "Verified" },
  rejected: { c: "bg-red-100 text-red-800", i: XCircle, l: "Rejected" },
};

function DocCell({ booking, docKey, onChange }) {
  const [busy, setBusy] = React.useState(null); // 'view'|'verify'|'reject'
  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const url = booking[`${docKey}_url`];
  const status = booking[`${docKey}_status`];
  const rejReason = booking[`${docKey}_reject_reason`];

  if (!url) {
    return <div className="text-xs text-muted-foreground italic">belum upload</div>;
  }

  const badge = STATUS_BADGE[status] || STATUS_BADGE.pending;
  const Icon = badge.i;

  const view = async () => {
    setBusy("view");
    try {
      const r = await api.get(`/files/${url}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch {
      toast.error("Gagal preview");
    } finally { setBusy(null); }
  };

  const act = async (action, reasonText) => {
    setBusy(action);
    try {
      const r = await api.patch(`/admin/bookings/${booking.id}/documents/${docKey}/verify`, {
        action, reason: reasonText || null,
      });
      toast.success(action === "verify" ? `${docKey} verified` : `${docKey} rejected`);
      setRejectOpen(false);
      setReason("");
      onChange(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-2" data-testid={`doc-cell-${booking.id}-${docKey}`}>
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-widest rounded-full px-2 py-1 ${badge.c}`}>
          <Icon className="h-3 w-3" /> {badge.l}
        </span>
        <button
          onClick={view}
          disabled={busy === "view"}
          className="p-1 rounded hover:bg-muted text-primary"
          title="Preview"
          data-testid={`view-doc-${booking.id}-${docKey}`}
        >
          {busy === "view" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
        </button>
      </div>
      {status === "rejected" && rejReason && (
        <p className="text-[10px] text-red-700 leading-tight">{rejReason}</p>
      )}
      {status !== "verified" && (
        <div className="flex gap-1">
          <button
            onClick={() => act("verify", null)}
            disabled={busy === "verify"}
            className="text-[10px] px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
            data-testid={`verify-doc-${booking.id}-${docKey}`}
          >
            {busy === "verify" ? "..." : "Verify"}
          </button>
          {status !== "rejected" && (
            <button
              onClick={() => setRejectOpen(true)}
              className="text-[10px] px-2 py-1 rounded bg-red-100 text-red-800 hover:bg-red-200"
              data-testid={`reject-doc-${booking.id}-${docKey}`}
            >
              Reject
            </button>
          )}
        </div>
      )}
      {rejectOpen && (
        <div className="mt-1 space-y-1">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Alasan tolak..."
            className="w-full text-xs px-2 py-1 border border-border rounded"
            data-testid={`reject-reason-${booking.id}-${docKey}`}
          />
          <div className="flex gap-1">
            <button
              disabled={!reason.trim() || busy === "reject"}
              onClick={() => act("reject", reason.trim())}
              className="text-[10px] px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              data-testid={`confirm-reject-${booking.id}-${docKey}`}
            >
              {busy === "reject" ? "..." : "Kirim"}
            </button>
            <button onClick={() => { setRejectOpen(false); setReason(""); }} className="text-[10px] px-2 py-1 rounded border border-border hover:bg-muted">
              Batal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DocumentsTab() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [filter, setFilter] = React.useState("all");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/bookings-documents");
      setRows(r.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const updateRow = (updated) =>
    setRows((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));

  const filtered = React.useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "pending") {
      return rows.filter((b) => DOCS.some((d) => b[`${d.key}_url`] && b[`${d.key}_status`] === "pending"));
    }
    if (filter === "rejected") {
      return rows.filter((b) => DOCS.some((d) => b[`${d.key}_status`] === "rejected"));
    }
    if (filter === "complete") {
      return rows.filter((b) => DOCS.every((d) => b[`${d.key}_status`] === "verified"));
    }
    return rows;
  }, [rows, filter]);

  const counts = React.useMemo(() => {
    const c = { all: rows.length, pending: 0, rejected: 0, complete: 0 };
    rows.forEach((b) => {
      if (DOCS.some((d) => b[`${d.key}_url`] && b[`${d.key}_status`] === "pending")) c.pending += 1;
      if (DOCS.some((d) => b[`${d.key}_status`] === "rejected")) c.rejected += 1;
      if (DOCS.every((d) => b[`${d.key}_status`] === "verified")) c.complete += 1;
    });
    return c;
  }, [rows]);

  const FilterTab = ({ v, label, cls }) => (
    <button
      onClick={() => setFilter(v)}
      data-testid={`doc-filter-${v}`}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-medium ${cls} ${filter === v ? "ring-2 ring-offset-1 ring-accent" : ""}`}
    >
      {label}
      <span className="min-w-[22px] text-center rounded-full bg-white/70 border border-current/20 px-1.5 py-0.5 text-[10px]">
        {counts[v] || 0}
      </span>
    </button>
  );

  return (
    <TabsContent value="documents" data-testid="tab-documents-content">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-primary">Verifikasi Dokumen Jamaah</h2>
          <p className="text-sm text-muted-foreground mt-1">Cek paspor, KTP, dan sertifikat vaksin meningitis yang diupload jamaah.</p>
        </div>
        <button onClick={load} disabled={loading} className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border hover:bg-muted">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <FilterTab v="all" label="Semua" cls="border-primary/30 text-primary bg-white hover:bg-muted" />
        <FilterTab v="pending" label="Perlu Diverifikasi" cls="border-amber-200 text-amber-900 bg-amber-50 hover:bg-amber-100" />
        <FilterTab v="rejected" label="Ada Ditolak" cls="border-red-200 text-red-800 bg-red-50 hover:bg-red-100" />
        <FilterTab v="complete" label="Lengkap Verified" cls="border-emerald-200 text-emerald-800 bg-emerald-50 hover:bg-emerald-100" />
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-left">
              <tr>
                <th className="p-4 font-medium">Jamaah</th>
                <th className="p-4 font-medium">Paket</th>
                {DOCS.map((d) => <th key={d.key} className="p-4 font-medium">{d.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="7" className="p-10 text-center text-muted-foreground">Tidak ada booking dengan dokumen di filter ini.</td></tr>
              )}
              {filtered.map((b) => (
                <tr key={b.id} className="border-t border-border align-top" data-testid={`doc-row-${b.id}`}>
                  <td className="p-4">
                    <div className="font-medium">{b.name}</div>
                    <div className="text-xs text-muted-foreground">{b.email}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-sm">{b.package_name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{b.status}</div>
                  </td>
                  {DOCS.map((d) => (
                    <td key={d.key} className="p-4">
                      <DocCell booking={b} docKey={d.key} onChange={updateRow} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </TabsContent>
  );
}
